// GET /assets/:id/state — the asset's current state ("estado actual del
// activo", ADR-0137): per source report, its sections with per-item
// verdicts, counts and conformance. Positives are derived at read time
// from the per-item verdicts (never materialized as findings), so this
// shape mirrors core-engine's buildChecklistItemsState output.
//
// At most one source: the store's latest terminal acta, whatever its
// checklist template (`sources` stays an array for wire stability).
// Verdicts never carry over between actas — an item absent
// from that acta reads as unreviewed, not as whatever an older acta said.
import { z } from "zod";
// ok = positive; low/med/high = negative by weight within the recipe
// (rank order among the negative severities, never severity slugs).
export const assetVerdictTone = z.enum(["ok", "low", "med", "high"]);
export const assetVerdict = z.object({
    // A recipe severity key; the positive one for a passing item.
    key: z.string().min(1),
    label: z.string().min(1),
    tone: assetVerdictTone,
});
// Distinguishes "No aplica" from "Sin revisión" when verdict is null.
export const assetItemConformance = z.enum([
    "positive",
    "negative",
    "not_applicable",
    "unreviewed",
]);
export const assetStateItem = z.object({
    key: z.string().min(1),
    label: z.string(),
    conformance: assetItemConformance,
    verdict: assetVerdict.nullable(),
    description: z.string().nullable(),
    photoUrls: z.array(z.string().url()),
});
export const assetStateSection = z.object({
    key: z.string().min(1),
    label: z.string(),
    counts: z.object({
        positive: z.number().int().nonnegative(),
        negative: z.number().int().nonnegative(),
        notApplicable: z.number().int().nonnegative(),
        unreviewed: z.number().int().nonnegative(),
    }),
    // positive ÷ (positive + negative), rounded 0–100. Null when nothing
    // in the section was reviewed as either — never a default 0 or 100.
    conformancePct: z.number().int().min(0).max(100).nullable(),
    items: z.array(assetStateItem),
});
export const assetStateSource = z.object({
    reportId: z.string().uuid(),
    reportType: z.string().min(1),
    templateLabel: z.string().nullable(),
    // Terminal timestamp (closedAt, or signedAt for signature closure).
    closedAt: z.string().datetime(),
    score: z.number().nullable(),
    sections: z.array(assetStateSection),
});
export const assetStateResponse = z.object({
    assetId: z.string().uuid(),
    sources: z.array(assetStateSource),
    // True when a source acta predates per-item photo capture (ADR-0088):
    // its photos exist but none is attributed to an item, so they render
    // «Sin evaluar». No backfill — the client shows a note instead.
    legacyUnattributedPhotos: z.boolean(),
});
//# sourceMappingURL=asset-state.js.map